/* Variant cascade - direction G.
 *
 * Three options (Cable Type -> Cable Length -> Rated Power), and not every
 * combination exists. Four states, and which one a value gets is the whole
 * point of the component:
 *
 *   selected      the current choice
 *   unavailable   the value exists, but not with what is already chosen.
 *                 STILL CLICKABLE - picking it re-resolves the later options.
 *   soldout       no purchasable variant anywhere carries this value.
 *                 Greyed ON ARRIVAL, never after three clicks.
 *   locked        an earlier option has not been answered yet
 *
 * Expects, in the section:
 *   <div data-cascade data-variants='[...]'>          JSON emitted by Liquid
 *     <fieldset data-option-index="0"><button data-value="...">
 *   Price node: [data-price], with a [data-price-from] label to hide/show.
 */
(function () {
  function money(cents, format) {
    var v = (cents / 100).toFixed(2).replace(/\.00$/, "");
    return (format || "\u00A3{{amount}}").replace("{{amount}}", v);
  }

  function Cascade(root) {
    this.root = root;
    /* The hidden id input and the add-to-cart button live in the product form,
       which WRAPS this component; the gallery sits outside the form entirely.
       Scoping these to `root` finds none of them, which is why the picker was
       cosmetic. Resolve them against the form and the document instead. */
    this.form = root.closest("form") || document;
    this.swapToken = 0;
    try { this.variants = JSON.parse(root.getAttribute("data-variants") || "[]"); }
    catch (e) { this.variants = []; }
    this.format = root.getAttribute("data-money-format") || "\u00A3{{amount}}";
    this.groups = Array.prototype.slice.call(root.querySelectorAll("[data-option-index]"));
    this.selection = this.groups.map(function () { return null; });

    // Values no purchasable variant carries at all - known up front, so they
    // can grey on arrival rather than after the shopper has tried them.
    this.everAvailable = this.groups.map(function (g, i) {
      var set = {};
      this.variants.forEach(function (v) {
        if (v.available) set[v.options[i]] = true;
      });
      return set;
    }, this);

    this.bind();
    this.guardSubmit();
    this.preselect();
    this.render();
  }

  Cascade.prototype.bind = function () {
    var self = this;
    this.root.addEventListener("click", function (e) {
      var reopen = e.target.closest("[data-step-change]");
      if (reopen) {
        var rg = reopen.closest("[data-option-index]");
        if (rg) { rg.classList.add("is-open"); reopen.hidden = true; }
        return;
      }
      var btn = e.target.closest("[data-value]");
      if (!btn || btn.disabled) return;
      var group = btn.closest("[data-option-index]");
      if (!group) return;
      e.preventDefault();

      var i = parseInt(group.getAttribute("data-option-index"), 10);
      var value = btn.getAttribute("data-value");
      if (self.selection[i] === value) return;
      self.selection[i] = value;

      // Choosing an earlier option can invalidate later ones. Clear anything
      // downstream that no longer resolves rather than silently keeping it.
      for (var j = i + 1; j < self.selection.length; j++) {
        if (self.selection[j] && !self.possible(j, self.selection[j])) {
          self.selection[j] = null;
        }
      }
      self.render();
    });
  };

  // Preselect from ?variant= so the reg lookup deep link lands correctly.
  Cascade.prototype.preselect = function () {
    var id = new URLSearchParams(window.location.search).get("variant");
    var match = id && this.variants.filter(function (v) { return String(v.id) === id; })[0];
    if (!match) {
      match = this.variants.filter(function (v) { return v.available; })[0];
    }
    if (match) this.selection = match.options.slice();
  };

  // Is this value reachable, given every OTHER answered option?
  Cascade.prototype.possible = function (i, value) {
    var sel = this.selection;
    return this.variants.some(function (v) {
      if (!v.available) return false;
      if (v.options[i] !== value) return false;
      for (var j = 0; j < sel.length; j++) {
        if (j !== i && sel[j] && v.options[j] !== sel[j]) return false;
      }
      return true;
    });
  };

  Cascade.prototype.current = function () {
    var sel = this.selection;
    if (sel.some(function (s) { return !s; })) return null;
    return this.variants.filter(function (v) {
      return v.options.every(function (o, i) { return o === sel[i]; });
    })[0] || null;
  };

  Cascade.prototype.render = function () {
    var self = this;
    var priorAnswered = true;

    this.groups.forEach(function (group, i) {
      var buttons = group.querySelectorAll("[data-value]");
      Array.prototype.forEach.call(buttons, function (btn) {
        var value = btn.getAttribute("data-value");
        btn.classList.remove("is-selected", "is-unavailable", "is-soldout", "is-locked");
        btn.disabled = false;
        btn.removeAttribute("aria-disabled");

        if (!self.everAvailable[i][value]) {
          btn.classList.add("is-soldout");
          btn.disabled = true;
          btn.setAttribute("aria-disabled", "true");
        } else if (!priorAnswered) {
          btn.classList.add("is-locked");
          btn.disabled = true;
          btn.setAttribute("aria-disabled", "true");
        } else if (self.selection[i] === value) {
          btn.classList.add("is-selected");
          btn.setAttribute("aria-checked", "true");
        } else if (!self.possible(i, value)) {
          // Recoverable - leave it clickable so the shopper can pivot.
          btn.classList.add("is-unavailable");
          btn.setAttribute("aria-checked", "false");
        } else {
          btn.setAttribute("aria-checked", "false");
        }
      });

      var summary = group.querySelector("[data-option-answer]");
      if (summary) summary.textContent = self.selection[i] || "";
      /* Progressive disclosure per the design: an answered step collapses to a
         summary row with a Change affordance; is-open (set by that button)
         reopens it without losing the selection. */
      var answered = !!self.selection[i];
      group.classList.toggle("is-answered", answered);
      if (answered) group.classList.remove("is-open");
      var change = group.querySelector("[data-step-change]");
      if (change) change.hidden = !answered;

      if (!self.selection[i]) priorAnswered = false;
    });

    this.syncProductForm();
    this.renderPrice();
    this.renderMedia();
    var sticky = document.querySelector("[data-sticky-variant]");
    if (sticky) sticky.textContent = this.selection.filter(Boolean).join(", ");
  };

  /* Hand the selection to the live ProductForm, which still owns price, Klarna,
     compare-at, the running total, the gallery swap and the add-to-cart AJAX.
     Without this both scripts write the same nodes and the last one to run wins,
     which is how the picker ended up pinned to the first variant. */
  Cascade.prototype.syncProductForm = function () {
    var self = this;
    var pf = this.form && this.form.__productForm;
    if (!pf) {
      /* ProductForm has not initialised yet. Retry once the current task and
         the next frame have cleared, then give up quietly. */
      if (!this._syncRetries) this._syncRetries = 0;
      if (this._syncRetries < 3) {
        this._syncRetries++;
        (window.requestAnimationFrame || setTimeout)(function () { self.syncProductForm(); });
      }
      return;
    }
    pf.options = this.selection.slice();
    if (typeof pf.update === "function") pf.update();
  };

  Cascade.prototype.renderPrice = function () {
    var node = this.root.querySelector("[data-price]");
    if (!node) return;
    var v = this.current();
    var fromLabel = this.root.querySelector("[data-price-from]");

    if (v) {
      node.textContent = money(v.price, this.format);
      if (fromLabel) fromLabel.hidden = true;
    } else {
      // "From X" until the selection resolves to exactly one variant.
      var lowest = this.variants
        .filter(function (x) { return x.available; })
        .reduce(function (a, x) { return a === null || x.price < a ? x.price : a; }, null);
      if (lowest !== null) node.textContent = money(lowest, this.format);
      if (fromLabel) fromLabel.hidden = false;
    }

    var idField = this.form.querySelector("[name='id']");
    if (idField) idField.value = v ? v.id : "";

    var submit = this.form.querySelector("[data-add-to-cart]");
    if (submit) {
      submit.disabled = !v;
      submit.textContent = v
        ? (submit.getAttribute("data-label-add") || "Add to basket")
        : (submit.getAttribute("data-label-choose") || "Choose your options");
    }

    if (v && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set("variant", v.id);
      window.history.replaceState({}, "", url);
    }
  };

  // Only the connector is visible in a photograph - you cannot see 16amp
  // against 32amp - so media filters on cable type alone. Untagged media
  // (car shots, carry case) always show.
  Cascade.prototype.renderMedia = function () {
    var self = this;
    /* We own the hero swap even when ProductForm is present. Its own
       showVariantImage() preloads a DETACHED Image with a srcset and awaits
       decode(); in Chrome that promise never settles for an out-of-document
       image, so its apply() never runs and the picture never changes. The
       version below races decode against a timeout so the swap always lands. */
    /* Only the connector is visible in a photograph - you cannot see 16amp
       against 32amp - so thumbs filter on cable type alone. Untagged media
       (car shots, carry case) always show. */
    var thumbs = document.querySelectorAll("[data-gallery-thumb]");
    var type = this.selection[0];
    Array.prototype.forEach.call(thumbs, function (t) {
      var mt = t.getAttribute("data-cable-type");
      /* Thumbs carry short names ("Type 2 to 3 Pin"); options carry long ones
         ("Type 2 to 3 Pin Charging Cable"). Exact match hid every tagged thumb. */
      t.hidden = !!(type && mt && type.indexOf(mt) !== 0);
    });

    /* Hero swap. Technique ported verbatim from the live product-form.js:
       decode the next image off-screen so the browser never tears down the
       current one, and stamp each swap so a stale request that lands late is
       discarded rather than winning. is-swapping is styled in theme.css. */
    var v = this.current();
    if (!v || !v.featured_image || !v.featured_image.src) return;
    var hero = document.querySelector("[data-gallery-hero] img");
    if (!hero) return;

    var base = v.featured_image.src.split("?")[0];
    if (hero.getAttribute("src") && hero.getAttribute("src").split("?")[0] === base) return;

    var ver = (v.featured_image.src.split("?")[1] || "").replace(/&?width=\d+/, "");
    var q = ver ? ("?" + ver + "&") : "?";
    var srcset = base + q + "width=600 600w, " + base + q + "width=1000 1000w, " +
                 base + q + "width=1400 1400w, " + base + q + "width=2000 2000w";
    var src = base + q + "width=1400";
    var token = ++this.swapToken;

    function apply() {
      if (self.swapToken !== token) return;
      hero.setAttribute("srcset", srcset);
      hero.setAttribute("sizes", hero.getAttribute("sizes") || "(min-width: 1024px) 60vw, 100vw");
      hero.setAttribute("src", src);
      if (v.featured_image.alt) hero.setAttribute("alt", v.featured_image.alt);
      hero.classList.remove("is-swapping");
    }
    var pre = new Image();
    pre.sizes = hero.getAttribute("sizes") || "(min-width: 1024px) 60vw, 100vw";
    pre.src = src;                 /* src only - a srcset here is what hangs decode() */
    hero.classList.add("is-swapping");
    var settled = false;
    function once() { if (!settled) { settled = true; apply(); } }
    setTimeout(once, 1200);        /* never leave the image mid-swap */
    if (pre.decode) pre.decode().then(once).catch(once);
    else if (pre.complete) once();
    else { pre.onload = once; pre.onerror = once; }
  };

  /* Belt and braces: product-form.js also seeds the hidden id once at load.
     Whichever script initialises last would otherwise win, so re-assert the
     resolved variant at submit time, when it is the only thing that matters. */
  Cascade.prototype.guardSubmit = function () {
    var self = this;
    if (this.form === document || !this.form.addEventListener) return;
    this.form.addEventListener("submit", function (e) {
      var v = self.current();
      var idField = self.form.querySelector("[name='id']");
      if (!v) { e.preventDefault(); return; }
      if (idField) idField.value = v.id;
      /* The cascade owns the add itself. The legacy ProductForm's AJAX handler
         does not reliably bind on this form, and a native POST forces a full
         page load. Mirrors product-form.js semantics: the addon radio becomes
         a real second line, cart:updated refreshes the drawer and badge. */
      e.preventDefault();
      e.stopPropagation();
      var qtyEl = self.form.querySelector("[name='quantity']");
      var items = [{ id: v.id, quantity: Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1) }];
      var addon = self.form.querySelector("input[name='addon']:checked");
      if (addon && addon.value) items.push({ id: parseInt(addon.value, 10), quantity: 1 });
      var submitBtn = self.form.querySelector("[data-add-to-cart]");
      if (submitBtn) submitBtn.disabled = true;
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) throw new Error("add failed " + r.status);
        return r.json();
      }).then(function () {
        if (submitBtn) submitBtn.disabled = false;
        document.dispatchEvent(new CustomEvent("cart:updated"));
        var drawer = document.querySelector("[data-cart-drawer]");
        if (drawer) {
          drawer.setAttribute("aria-hidden", "false");
          document.body.classList.add("is-open");
        }
        fetch("/cart.js").then(function (r) { return r.json(); }).then(function (cart) {
          document.querySelectorAll("[data-cart-count]").forEach(function (n) {
            n.textContent = cart.item_count;
          });
        });
      }).catch(function () {
        /* Fall back to the cart page rather than swallowing the purchase. */
        window.location.href = "/cart";
      });
    }, true);
  };

  /* Clicking a thumbnail swaps the hero. Bound once, delegated. */
  var thumbClick = false;
  function bindThumbs() {
    if (thumbClick) return; thumbClick = true;
    document.addEventListener("click", function (e) {
      var t = e.target.closest("[data-gallery-thumb]");
      if (!t) return;
      var hero = document.querySelector("[data-gallery-hero] img");
      if (!hero) return;
      var src = t.getAttribute("data-src");
      if (!src) return;
      hero.setAttribute("srcset", t.getAttribute("data-srcset") || "");
      hero.setAttribute("src", src);
      document.querySelectorAll("[data-gallery-thumb].active").forEach(function (x) { x.classList.remove("active"); });
      t.classList.add("active");
    });
  }
  bindThumbs();

  function init() {
    document.querySelectorAll("[data-cascade]").forEach(function (el) {
      if (!el.__cascade) el.__cascade = new Cascade(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else { init(); }
  document.addEventListener("shopify:section:load", init);
})();
